import { planOf } from '@barso/shared';
import { Link } from 'react-router';
import { useAuth } from '../auth/AuthContext';
import { ContinueRegister } from '../components/ContinueRegister';
import { Accordion, type AccordionItem } from '../components/Accordion';
import { FlowArt, HeroArt, Icon, IconBadge, type IconName } from '../components/icons';
import { Card, LinkButton } from '../components/ui';
import { euro } from '../format';
import { isNativeApp } from '../native';
import { HomePage } from './home';
import { NativeStart } from './nativeStart';

/**
 * Startseite für Gäste (WEB-01, Entscheidung E8): erklärt BARSO kurz und führt zur Registrierung, zum
 * Basar-Kalender oder zur öffentlichen Anmeldung über einen Basar-Link. Angemeldete Personen sehen
 * stattdessen ihre Organisationen (`HomePage`) unter derselben Adresse (`/`).
 *
 * Bewusst kurz (Welle 5): ein Nutzensatz, vier Vorteile, vier Schritte, wenige Fragen. Details stehen auf
 * der Funktionsseite (`/funktionen`). Preise kommen aus `PRICE_PLANS`, hier steht nur die Startstufe.
 */
export function RootPage() {
  const { status } = useAuth();
  if (isNativeApp()) return <NativeStart />;
  if (status === 'authenticated') return <HomePage />;
  return <LandingPage />;
}

/** Startstufe aus der Preisliste (`PRICE_PLANS`), damit die Zahl nie von der Anwendung abweicht. */
function freeLimitLabel(): string {
  const limit = planOf('free').itemLimit;
  return limit === null ? 'unbegrenzt' : `bis ${String(limit)} Teile`;
}

/** Vier Kernvorteile, je ein Satz in einfacher Sprache. */
const BENEFITS: { icon: IconName; title: string; text: string }[] = [
  {
    icon: 'offline',
    title: 'Kasse auch ohne Internet',
    text: 'Verkäufe werden auf dem Gerät gespeichert und später übertragen. Es wird einfach weiter kassiert.',
  },
  {
    icon: 'users',
    title: 'Verkäufer melden sich selbst an',
    text: 'Sie erfassen ihre Artikel selbst. Niemand muss Listen abtippen.',
  },
  {
    icon: 'euro',
    title: 'Abrechnung auf den Cent',
    text: 'Die Verkaufsübersicht ist während des Basars aktuell und stimmt auf den Cent genau.',
  },
  {
    icon: 'browser',
    title: 'Ohne Installation',
    text: 'BARSO läuft im Browser auf Laptop, Tablet oder Handy.',
  },
];

/** Vier Schritte des Basars; Titel und ein Satz. */
const STEPS: { icon: IconName; label: string; text: string }[] = [
  {
    icon: 'calendar',
    label: 'Basar anlegen',
    text: 'Termin, Ort und Regeln eintragen. Dann den Link teilen.',
  },
  {
    icon: 'users',
    label: 'Verkäufer melden sich an',
    text: 'Sie erfassen ihre Artikel selbst.',
  },
  { icon: 'scan', label: 'Verkaufen', text: 'Die Kasse läuft im Browser, auch ohne Netz.' },
  {
    icon: 'check',
    label: 'Abrechnen',
    text: 'Verkaufsübersicht je Verkäufer. Die Auszahlung wird abgehakt.',
  },
];

/** Häufige Fragen: nur die wichtigsten. */
function faq(): AccordionItem[] {
  const unlimited = planOf('unlimited');
  return [
    {
      question: 'Was kostet BARSO?',
      answer: (
        <p>
          Kleine Basare {freeLimitLabel()} sind kostenlos. Größere Basare zahlen einmal je Basar{' '}
          {euro(unlimited.priceGrossCents)} ohne Obergrenze bei der Zahl der Teile. Es gibt kein
          Abo, alle Preise sind Endpreise inklusive Umsatzsteuer.{' '}
          <Link to="/funktionen#preise">Alle Preise ansehen</Link>
        </p>
      ),
    },
    {
      question: 'Was passiert, wenn das Internet ausfällt?',
      answer: (
        <p>
          Die Kasse speichert jeden Verkauf zuerst auf dem Gerät und überträgt ihn später, sobald
          wieder eine Verbindung besteht. Es wird einfach weiter kassiert.
        </p>
      ),
    },
    {
      question: 'Was passiert mit unverkaufter Ware?',
      answer: (
        <p>
          Sie wird bei der Rückgabe nach Nummer und Farbe zurückgegeben. Dafür muss nichts an der
          Station gescannt werden.
        </p>
      ),
    },
    {
      question: 'Wie werden Verkäufer ausgezahlt?',
      answer: (
        <p>
          Je nach Einstellung des Basars bar oder per Überweisung. Die Auszahlung organisiert Ihr
          Team selbst. BARSO wickelt kein Geld ab.
        </p>
      ),
    },
    {
      question: 'Können Käufer mit Karte zahlen?',
      answer: (
        <p>
          Nein. Bezahlt wird am Basar-Tag bar. BARSO wickelt keine Zahlungen ab. Die Kasse kann eine
          Kartenzahlung nur als Markierung festhalten.
        </p>
      ),
    },
    {
      question: 'Ist BARSO ein Kassensystem fürs Finanzamt?',
      answer: (
        <p>
          Nein. BARSO ist kein zertifiziertes Kassensystem. Die Verkaufsübersicht ist eine
          Auswertung der Verkäufe und kein steuerlicher Beleg.
        </p>
      ),
    },
  ];
}

export function LandingPage() {
  return (
    <div className="stack landing">
      {/* Eine Kasse ohne Konto (Schnellzugang) findet hier zurück, ohne Anmeldung */}
      <ContinueRegister deviceOnly />
      <section className="hero hero--split" aria-labelledby="nutzen">
        <div className="stack">
          <p className="eyebrow">Software für Nummernbasare</p>
          <h1 id="nutzen">Nummernbasare einfach organisieren</h1>
          <p className="hero__lead">
            BARSO führt Ihren Basar von der Anmeldung bis zur Abrechnung. Die Kasse läuft auch ohne
            Internet weiter.
          </p>
          <div className="row">
            <Link className="btn" to="/registrieren">
              Kostenlos starten
            </Link>
            <LinkButton icon="pin" variant="secondary" to="/basare-entdecken">
              Basare entdecken
            </LinkButton>
          </div>
        </div>
        <HeroArt />
      </section>

      <section className="stack" aria-labelledby="werte">
        <div className="section-head stack">
          <h2 id="werte">Das bringt BARSO</h2>
        </div>
        <div className="grid grid--2">
          {BENEFITS.map((benefit) => (
            <Card key={benefit.title}>
              <div className="benefit">
                <IconBadge name={benefit.icon} large />
                <div className="stack">
                  <h3>{benefit.title}</h3>
                  <p className="muted">{benefit.text}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </section>

      <section className="stack" aria-labelledby="ablauf">
        <div className="section-head stack">
          <p className="eyebrow">So funktioniert es</p>
          <h2 id="ablauf">So läuft ein Basar ab</h2>
          <FlowArt />
        </div>
        <ol className="steps">
          {STEPS.map((step, index) => (
            <li key={step.label}>
              <span className="steps__badge">
                <Icon name={step.icon} size={28} />
                <span className="steps__number" aria-hidden="true">
                  {index + 1}
                </span>
              </span>
              <strong>{step.label}</strong>
              <span className="muted">{step.text}</span>
            </li>
          ))}
        </ol>
      </section>

      <section className="stack" aria-labelledby="verkaeufer-titel">
        <p className="eyebrow">Für Verkäuferinnen und Verkäufer</p>
        <h2 id="verkaeufer-titel">Einfach. Digital. Unkompliziert.</h2>
        <p className="muted">
          Anmelden, Artikel erfassen und den eigenen Verkaufsstand verfolgen, alles über den Link
          des Basars. Dafür brauchen Sie ein kostenloses Konto.
        </p>
        <p>
          <Link to="/funktionen#verkaeufer">So funktioniert es für Verkäufer</Link>
        </p>
      </section>

      <p className="center muted">
        Für Kitas, Schulen, Vereine und Gemeinden, die ihren Basar ehrenamtlich organisieren.
      </p>

      <section className="stack" aria-labelledby="fragen">
        <div className="section-head stack">
          <p className="eyebrow">Häufige Fragen</p>
          <h2 id="fragen">Gut zu wissen</h2>
        </div>
        <Accordion items={faq()} />
      </section>

      <section className="cta-band stack" aria-labelledby="einladung">
        <h2 id="einladung">Bereit für Ihren nächsten Basar?</h2>
        <p>
          Konto anlegen, Basar einrichten und den Link teilen. Haben Sie schon einen Einladungslink?
          Öffnen Sie ihn direkt.
        </p>
        <div className="row">
          <LinkButton icon="plus" to="/registrieren">
            Konto anlegen
          </LinkButton>
          <LinkButton icon="lock" variant="secondary" to="/anmelden">
            Schon ein Konto? Anmelden
          </LinkButton>
        </div>
        <p className="muted">
          Hosting in Deutschland. <Link to="/datenschutz">Datenschutzerklärung</Link>
        </p>
      </section>
    </div>
  );
}
